import { HttpRequest } from '@angular/common/http';
import { environment } from '@env/environment';
import { rebaseUrlToBaseDomain, resolveActiveBaseDomain } from '@domain/base-domain';

/**
 * Roots whose requests carry the JWT.
 *
 * Resolved per call rather than once at module load because the HR root depends on the base
 * domain the app is currently served from: `environment.hrUrl` names one specific base domain,
 * and callers reach HR on whichever one the user arrived on. Both forms are listed so a request
 * is matched whether it was built from the configured URL or the rebased one.
 *
 * `additionalApiRoots` covers the same problem for the Cintra APIs from the other side: the roots
 * above are resolved to the base domain serving this page, so the other domain's roots are listed
 * too and a URL built against either one still carries the JWT.
 *
 * Empty values are dropped — `url.includes('')` is true for every URL, which would attach the
 * JWT to third-party requests.
 */
function apiRoots(): string[] {
  const activeBaseDomain = resolveActiveBaseDomain(location.hostname, environment.baseDomains);

  return [
    environment.apiRoot,
    environment.employeeHubApiRoot,
    environment.reportingApiRoot,
    environment.hrUrl,
    rebaseUrlToBaseDomain(environment.hrUrl, activeBaseDomain, environment.baseDomains),
    ...(environment.additionalApiRoots ?? [])
  ].filter(Boolean);
}

function isApiRoot(url: string): boolean {
  return apiRoots().some((apiRoot) => url.includes(apiRoot));
}

/**
 * Determines if a request should be intercepted for JWT token addition
 * @param request The HTTP request to check
 * @returns true if the request should be intercepted, false otherwise
 */
export function isCloudApiUrl(request: HttpRequest<any>): boolean {
  return isApiRoot(request.url);
}
